import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatBitsPerSecondAxis,
    formatTimeTick,
    throughputUnitToBitsPerSecondMultiplier,
} from "../../helpers/chart_common";
import {isRedacted} from "../../helpers/redact";
import {getTopLevelItems, parseShaperChildrenBlob} from "../../helpers/shaper_children";
import {debugLog} from '../../helpers/debug_log';

export class ShaperCombinedThroughput extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Shaper Child Throughput";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper Combined Throughput</h5><p>Child site throughput.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCombinedThroughputGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "SiteShaperPage" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_children") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        const parsed = parseShaperChildrenBlob(msg.Data.blob);
        window.isFlatNetwork = parsed.isFlat;
        this.graph.update(parsed);
        this.lastGraphState = parsed;
    }

}

class ShaperCombinedThroughputGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Bandwidth", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(payload) {
        this.chart.hideLoading();

        const dataset = getTopLevelItems(payload);
        const sites = dataset.topLevel || {};
        const topDownload = dataset.topDownload || [];
        const topUpload = dataset.topUpload || [];

        const topDownloadSet = new Set(topDownload);
        const topUploadSet = new Set(topUpload.length ? topUpload : topDownload);

        let series = [];
        let otherDown = [];
        let otherUp = [];

        const legends = new Set();
        this.option.legend.data = [];
        this.option.xAxis.data = [];

        let n = 0;
        const addLegend = (name) => {
            if (!legends.has(name)) {
                legends.add(name);
                this.option.legend.data.push(name);
            }
        };

        for (const [site, value] of Object.entries(sites)) {
            if (!this.option.xAxis.data.length) {
                this.option.xAxis.data = value.body.ticks || [];
            }
            const includeDown = !topDownloadSet.size || topDownloadSet.has(site);
            const includeUp = !topUploadSet.size || topUploadSet.has(site);
            const name = value.header.name;
            const throughputMultiplier = throughputUnitToBitsPerSecondMultiplier(
                value.body?.throughput_unit,
            );

            if (includeDown) {
                const d = (value.body.download_bytes_median || []).map(
                    (v) => (v || 0) * throughputMultiplier,
                );
                series.push({
                    name: name,
                    type: "line",
                    data: d,
                    areaStyle: { },
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    stack: 'down',
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                });
                addLegend(name);
            } else {
                for (let i=0; i<(value.body.download_bytes_median || []).length; i++) {
                    if (otherDown.length <= i) {
                        otherDown.push(0);
                    }
                    otherDown[i] += (value.body.download_bytes_median[i] || 0) * throughputMultiplier;
                }
            }

            if (includeUp) {
                const dUp = (value.body.upload_bytes_median || []).map(
                    (v) => 0 - ((v || 0) * throughputMultiplier),
                );
                series.push({
                    name: name,
                    type: "line",
                    data: dUp,
                    areaStyle: { },
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    stack: 'up',
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 1,
                    yAxisIndex: 0,
                });
                addLegend(name);
            } else {
                for (let i=0; i<(value.body.upload_bytes_median || []).length; i++) {
                    if (otherUp.length <= i) {
                        otherUp.push(0);
                    }
                    otherUp[i] += 0 - ((value.body.upload_bytes_median[i] || 0) * throughputMultiplier);
                }
            }
            if (includeDown || includeUp) {
                n += 1;
            }
        }

        if (otherDown.some((v) => v !== 0) || otherUp.some((v) => v !== 0)) {
            series.push({
                name: "Other",
                type: "line",
                data: otherDown,
                areaStyle: { },
                itemStyle: {
                    color: window.graphPalette[n]
                },
                stack: 'down',
                smooth: true,
                showSymbol: false,
                xAxisIndex: 0,
                yAxisIndex: 0,
            });
            series.push({
                name: "Other",
                type: "line",
                data: otherUp,
                areaStyle: { },
                itemStyle: {
                    color: window.graphPalette[n]
                },
                stack: 'up',
                smooth: true,
                showSymbol: false,
                xAxisIndex: 1,
                yAxisIndex: 0,
            });
            addLegend("Other");
        }

        this.option.series = series;
        applySplitDirectionAxes(this.option, {
            ticks: this.option.xAxis.data,
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatBitsPerSecondAxis,
        });
        
        // Apply redaction to legend if enabled
        if (isRedacted()) {
            this.option.legend.textStyle = {
                fontFamily: "Illegible"
            };
        } else {
            this.option.legend.textStyle = {};
        }
        
        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatBitsPerSecondAxis,
            formatter: (params) => {
                let result = `<div style="font-size: 10px;">${formatTimeTick(params[0].axisValue)}<br/>`;
                const downloadData = params.filter((p) => p.axisIndex === 0);
                const uploadData = params.filter((p) => p.axisIndex === 1);
                if (downloadData.length > 0) {
                    result += "<strong>Download</strong><br/>";
                    downloadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatBitsPerSecondAxis(item.value)}<br/>`;
                    });
                }
                if (uploadData.length > 0) {
                    result += "<strong>Upload</strong><br/>";
                    uploadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatBitsPerSecondAxis(item.value)}<br/>`;
                    });
                }
                result += "</div>";
                return result;
            },
        });

        // Add axis pointer configuration for highlighting
        this.option.axisPointer = {
            link: {xAxisIndex: 'all'},
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };
        
        finalizeSplitDirectionChart(this.option);
        debugLog(this.option);
        this.option && this.chart.setOption(this.option, true);

        debugLog(payload);
    }
}
